import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useIdioma, hayIdiomaGuardado } from '../i18n/idioma';

import './landing/landing.css';
import { refreshOnFontsReady, useSmoothScroll } from './landing/motion';
import Frase from './landing/Frase';
import { OFICIO, CASCO } from './landing/media';
import LandingNav from './landing/LandingNav';
import Hero from './landing/Hero';
import Reemplaza from './landing/Reemplaza';
import Producto from './landing/Producto';
import FotoBanda from './landing/FotoBanda';
import Pasos from './landing/Pasos';
import Academy from './landing/Academy';
import Tienda from './landing/Tienda';
import Preguntas from './landing/Preguntas';
import Cierre from './landing/Cierre';
import PieDePagina from './landing/PieDePagina';
import ModalAcceso from './landing/ModalAcceso';
import ModalIdioma from './landing/ModalIdioma';

/* ═══════════════════════════════════════════════════════════════════════════
   RAZA PRO — landing
   ═══════════════════════════════════════════════════════════════════════════

   Rediseño completo de agosto de 2026. El diagnóstico del anterior no era de
   color: eran diez secciones con la MISMA silueta (rótulo en versalitas →
   título centrado → rejilla de tarjetas con ícono). Por eso la página se leía
   como generada aunque los tokens estuvieran bien.

   El orden de abajo es la decisión de diseño principal — cada fold tiene una
   forma y una superficie distintas, y el ritmo de superficies está pensado
   para que ninguna se repita seguida:

     Hero .............. navy + vídeo      portada
     Reemplaza ......... navy              el problema, tipográfico
     Producto .......... blanco            el producto, maquetas pegadas
     Foto (oficio) ..... fotografía        respiro
     Pasos ............. blanco            secuencia con riel
     Academy ........... acero claro       editorial a dos columnas
     Tienda ............ blanco            lista de precios, fold corto
     Foto (fundador) ... fotografía        la cita
     Preguntas ......... acero claro       acordeón con filetes
     Cierre ............ navy              última palabra
     Pie ............... blanco

   Lo que se eliminó y NO debe volver: texto con degradado, el rótulo en
   versalitas encima de cada título, las cuatro rejillas de tarjetas idénticas,
   el mockup de navegador con semáforo, las tarjetas flotantes con animación de
   boya, la barra de métricas gigantes y la comparación en columnas rojo/verde.
   El porqué de cada una está en PRODUCT.md.
   ═══════════════════════════════════════════════════════════════════════════ */

const LandingPage: React.FC = () => {
    const { login, loading, authError, currentUser } = useAuth();
    const { t } = useIdioma();
    const navigate = useNavigate();
    const [mostrarAcceso, setMostrarAcceso] = useState(false);
    /* Se pregunta el idioma solo si NUNCA se eligió. Se decide una vez, al
       montar, y no en cada render: elegir guarda la preferencia, y releerla
       cerraría la ventana antes de que se vea la animación de salida. */
    const [preguntarIdioma, setPreguntarIdioma] = useState(() => !hayIdiomaGuardado());

    // Cuando la sesión queda lista tras abrir el diálogo, se pasa al dashboard.
    useEffect(() => {
        if (currentUser && mostrarAcceso) navigate('/dashboard');
    }, [currentUser, mostrarAcceso, navigate]);

    // Las fuentes web recomponen los titulares después del primer pintado; sin
    // este refresco, los ScrollTrigger quedan medidos contra alturas viejas.
    useEffect(() => {
        refreshOnFontsReady();
    }, []);

    // Desplazamiento con inercia para toda la página. Es lo que más cambia la
    // SENSACIÓN del sitio: todo lo atado al scroll deja de ir a tirones de rueda.
    // Se apaga solo con `prefers-reduced-motion` (ver motion.ts).
    useSmoothScroll();

    const accionPrincipal = useCallback(() => {
        if (currentUser) navigate('/dashboard');
        else setMostrarAcceso(true);
    }, [currentUser, navigate]);

    const conSesion = Boolean(currentUser);

    return (
        /* `overflow-x-clip` y NO `overflow-x-hidden`. `hidden` convierte a este
           div en contenedor de desplazamiento, y `position: sticky` se ancla al
           ancestro que hace scroll: la maqueta del fold amarillo se quedaba
           pegada a un contenedor del alto de la página entera, o sea nunca se
           pegaba. `clip` recorta igual el desborde horizontal sin crear ese
           contenedor. */
        <div className="rp min-h-dvh overflow-x-clip bg-white text-navy-950">
            <LandingNav signedIn={conSesion} onPrimary={accionPrincipal} />

            {/* `bg-navy-850`: la portada pegada se encoge un punto al quedar
                tapada y deja ver lo que hay detrás. Sin este fondo asomaba el
                blanco de la página por los cuatro lados. */}
            {/* `id` + `tabIndex={-1}`: destino del enlace de salto de la barra.
                Sin el `tabIndex`, el salto mueve la vista pero en varios
                navegadores el foco se queda arriba y el siguiente tabulador
                vuelve al menú — el salto no sirve de nada. */}
            <main id="contenido" tabIndex={-1} className="relative bg-navy-850">
                <Hero signedIn={conSesion} onPrimary={accionPrincipal} />

                {/* Todo lo que va DESPUÉS de la portada sube por encima de
                    ella. La portada es `sticky` y `z-0`: sin un contexto propio
                    con z mayor, un elemento pegado se pinta sobre sus hermanos
                    posteriores y taparía media página. */}
                <div className="relative z-10">
                    <Reemplaza />

                    <Producto />

                    <FotoBanda foto={OFICIO} prioritaria>
                        <p className="rp-h2 max-w-[18ch] text-white">
                            <Frase marca="banda">{t('lp.banda.oficio')}</Frase>
                        </p>
                    </FotoBanda>

                    <Pasos signedIn={conSesion} onPrimary={accionPrincipal} />

                    <Academy />

                    <Tienda />

                    {/* El fundador. Antes era un círculo amarillo con las iniciales
                        «AO» —un marcador de posición disfrazado de retrato— sobre
                        navy plano. Una fotografía real de obra dice lo mismo sin
                        fingir nada. */}
                    <FotoBanda foto={CASCO}>
                        <figure>
                            <blockquote className="rp-h2 max-w-[19ch] text-white">
                                <p><Frase marca="banda">{t('lp.banda.cita')}</Frase></p>
                            </blockquote>
                            <figcaption
                                data-copia
                                className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3"
                            >
                                <span className="text-[15px] text-white/80">
                                    <strong className="font-semibold text-white">
                                        Aurelio Olivera
                                    </strong>
                                    {t('lp.banda.autorRol')}
                                </span>
                                <a
                                    href="https://www.razaperronaacademy.com/elyarderoperron"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="rp-link text-brand-400"
                                >
                                    {t('lp.banda.historia')}
                                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                                </a>
                            </figcaption>
                        </figure>
                    </FotoBanda>

                    <Preguntas />

                    <Cierre signedIn={conSesion} onPrimary={accionPrincipal} />
                </div>
            </main>

            <PieDePagina />

            {preguntarIdioma && <ModalIdioma onListo={() => setPreguntarIdioma(false)} />}

            {mostrarAcceso && (
                <ModalAcceso
                    onClose={() => setMostrarAcceso(false)}
                    onLogin={login}
                    loading={loading}
                    error={authError}
                />
            )}
        </div>
    );
};

export default LandingPage;
